// PR-COACH (Block 3-A): client for the onboarding Sage Coach.
//
// Surfaces four operations to the Coach view:
//   • getStatus()        — entitlement + cap-state snapshot for the
//                          mode-pick card BEFORE any chat begins.
//   • approveStar(draft) — POST the user's approved STAR draft. Returns
//                          { success, capState, achievementId } on accept
//                          or { success:false, capReached:true, capState }
//                          when the cap is hit. (HTTP is always 200; the
//                          structured body is the truth — matches the
//                          ErrorResponse pattern axios doesn't throw on.)
//   • chat(message, …)   — non-streaming single turn. Used as a fallback
//                          when SSE is unavailable.
//   • chatStream(req, on*)— SSE streaming. Each event invokes the
//                          appropriate callback. Mirrors the SageHR
//                          stream reader so the UI logic feels familiar.

import { Get, Post } from "./ApiBase";
import * as env from "../utility/env";

export async function getOnboardingStatus() {
  return await Get("/api/GetSageOnboardingStatus");
}

export async function approveStar(draft) {
  // draft = { workKey, situation, task, action, result, headline,
  //           sourceNarrative, hasUnverifiedSpecifics }
  return await Post("/api/SageOnboardingApproveStar", draft);
}

export async function chat(req) {
  // req = { message, workKey, conversationId?, conversationHistory? }
  return await Post("/api/SageOnboardingChat", req);
}

// PR-GEN-STAR: one-shot AI draft of all four STAR fields from a single
// bullet + role context. Returns { success, data: { situation, task,
// action, result, source, hasUnverifiedSpecifics, disclosureAcknowledged } }.
export async function generateStarDraft(payload) {
  // payload = { bulletText, roleTitle, company, dateRange }
  return await Post("/api/GenerateStarDraft", payload);
}

// PR-GEN-STAR: persist the one-time acknowledgement of the disclosure modal.
export async function acknowledgeAiStarDisclosure() {
  return await Post("/api/AcknowledgeAiStarDisclosure", {});
}

// PR-COACH-STAR-UX (item 4): clean a raw voice/typed transcript for one STAR
// element before it's shown in the "looks right?" approval card. Fixes
// transcription errors, removes filler, and fixes grammar WITHOUT summarizing,
// genericizing, or inventing — so the Action/Result the user reaches via the
// manual-capture escape hatch read as clean prose like the Situation/Task that
// went through Sage's capture tool. Returns { success, data: { cleaned } }.
// The caller falls back to the raw text if this fails, so the user is never
// blocked.
export async function cleanStarElement(payload) {
  // payload = { element, rawText, accomplishment }
  return await Post("/api/CleanStarElement", payload);
}

// SSE streaming. The fetch + ReadableStream pattern (not EventSource)
// because EventSource doesn't support POST + cookies, and we need both.
//
// callbacks: { onStart, onThinking, onToolCall, onToolResult, onText,
//              onDraft, onCapState, onChips, onDone, onError }
// All callbacks are optional. The stream parser tolerates partial frames
// and resumes mid-frame on the next chunk.
export async function chatStream(req, callbacks) {
  const url = env.GetOrigin() + "/api/SageOnboardingChatStream";
  const cb = callbacks || {};

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req)
    });
  } catch (e) {
    if (cb.onError) cb.onError("Network error: " + (e && e.message));
    if (cb.onDone) cb.onDone();
    return;
  }

  if (!response.ok || !response.body) {
    if (cb.onError)
      cb.onError("Coach is unavailable right now. Try again in a moment.");
    if (cb.onDone) cb.onDone();
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    // Streaming loop. SSE wire format is `data: <json>\n\n` per event.
    // Buffer chunks until we see the double-newline terminator.
    // Loop driven by a sentinel so eslint's no-constant-condition is
    // happy without disabling the rule.
    let streaming = true;
    while (streaming) {
      const { done, value } = await reader.read();
      if (done) {
        streaming = false;
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      let frameEnd;
      while ((frameEnd = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, frameEnd);
        buffer = buffer.slice(frameEnd + 2);
        if (!frame.startsWith("data:")) continue;

        const jsonStr = frame.slice(5).trim();
        if (!jsonStr) continue;

        let ev;
        try {
          ev = JSON.parse(jsonStr);
        } catch (e) {
          // Skip malformed frame; SSE is resilient by design.
          continue;
        }

        switch (ev.type) {
          case "start":
            if (cb.onStart) cb.onStart(ev);
            break;
          case "thinking":
            if (cb.onThinking) cb.onThinking(ev);
            break;
          case "tool_call":
            if (cb.onToolCall) cb.onToolCall(ev);
            break;
          case "tool_result":
            if (cb.onToolResult) cb.onToolResult(ev);
            break;
          case "text":
            if (cb.onText) cb.onText(ev);
            break;
          case "draft":
            if (cb.onDraft) cb.onDraft(ev);
            break;
          // PR-COACH-V2: per-element capture event. Streamed when the
          // orchestrator's capture_element tool fires. UI shows the
          // approval card inline.
          case "element_capture":
            if (cb.onElementCapture) cb.onElementCapture(ev);
            break;
          case "cap_state":
            if (cb.onCapState) cb.onCapState(ev);
            break;
          case "chips":
            if (cb.onChips) cb.onChips(ev);
            break;
          case "error":
            if (cb.onError) cb.onError(ev.message);
            break;
          case "done":
            if (cb.onDone) cb.onDone(ev);
            break;
          default:
            // Unknown event types — ignore silently for forward-compat.
            break;
        }
      }
    }
  } catch (e) {
    if (cb.onError) cb.onError("Stream interrupted: " + (e && e.message));
  } finally {
    if (cb.onDone) cb.onDone();
  }
}
